// ─── OUTPUT FORMATTERS ───────────────────────────────────────────────────────
// Pure functions that transform content for display or export.

import { normalizeUrl } from './textProcessing.js';

// ─── Full Output Text Serializer ──────────────────────────────────────────────
// Converts a history entry's results object to plain text for clipboard/download.

export function getFullOutputTextForEntry(entry) {
  const results     = entry.results;
  const mode        = entry.mode;
  const entryCtaUrl = normalizeUrl(entry.params?.ctaUrl);
  if (!results) return '';
  // Structured content object from new backend
  if (results.content && typeof results.content === 'object') {
    return results.content.full_text || results.content.body || '';
  }
  // Legacy: content is a plain string
  if (results.content && typeof results.content === 'string') return results.content;
  if (results.raw) return results.raw;

  switch (mode) {
    case 'linkedin-post': {
      let t = results.fullPost || '';
      if (results.hook && t && !t.trim().startsWith(results.hook.trim())) t = results.hook + '\n\n' + t;
      if (results.hashtags?.length) t += '\n\n' + results.hashtags.map(h => '#' + h).join(' ');
      if (entryCtaUrl) t += '\n\n→ ' + entryCtaUrl;
      return t;
    }
    case 'linkedin-carousel': {
      let t = (results.slides || []).map(s => `--- SLIDE ${s.number} ---\n${s.headline}\n${s.body}\n[Visual: ${s.visualCue}]`).join('\n\n');
      if (results.companionPost) t += '\n\n--- POST ---\n' + results.companionPost;
      if (results.hashtags?.length) t += '\n\n' + results.hashtags.map(h => '#' + h).join(' ');
      if (entryCtaUrl) t += '\n\n→ ' + entryCtaUrl;
      return t;
    }
    case 'blog-seo': {
      let t = results.introduction || '';
      (results.sections || []).forEach(s => {
        t += `\n\n## ${s.h2}\n${s.content}`;
        (s.subsections || []).forEach(sub => { t += `\n\n### ${sub.h3}\n${sub.content}`; });
      });
      t += `\n\n## Conclusione\n${results.conclusion || ''}`;
      if (entryCtaUrl) t += `\n\n---\n→ Scopri di più: ${entryCtaUrl}`;
      if (results.faq?.length > 0) {
        t += `\n\n---\n\nFAQ\n`;
        results.faq.forEach((f, i) => { t += `\n${i + 1}. ${f.question}\n${f.answer}\n`; });
      }
      return t;
    }
    case 'email': {
      const eCh = entry.params?.emailChannel || 'email-classic';
      if (eCh === 'linkedin-newsletter') {
        let t = `# ${results.title || ''}\n`;
        if (results.subtitle) t += `*${results.subtitle}*\n`;
        t += `\n${results.introHook || ''}\n`;
        (results.sections || []).forEach(s => { t += `\n## ${s.h2 || ''}\n${s.body || ''}\n`; });
        if (results.keyTakeaways?.length) {
          t += `\n**Key Takeaways:**\n`;
          results.keyTakeaways.forEach((kt, i) => { t += `${i + 1}. ${kt}\n`; });
        }
        if (results.closingCTA)   t += `\n---\n${results.closingCTA}\n`;
        if (results.hashtags)     t += `\n${results.hashtags}`;
        if (results.companionPost) t += `\n\n--- COMPANION POST ---\n${results.companionPost}`;
        return t;
      }
      let t = `Subject: ${results.subjectLine || ''}\nPreheader: ${results.preheader || ''}`;
      t += `\n\n${results.greeting || ''}`;
      if (results.introHook) t += `\n\n${results.introHook}`;
      t += `\n\n${results.body || ''}`;
      if (results.cta) t += `\n\n[${results.cta}]`;
      if (entryCtaUrl) t += `(${entryCtaUrl})`;
      t += `\n\n${results.closing || ''}`;
      if (results.ps) t += `\n\nP.S. ${results.ps}`;
      return t;
    }
    case 'case-study': {
      let t = `## ${results.companyOverview || ''}\n\n`;
      t += `**Progetto:** ${results.projectBrief || ''}\n\n`;
      if (results.challenge) {
        t += `## SFIDA\n${results.challenge.title || ''}\n${results.challenge.description || ''}\n`;
        (results.challenge.painPoints || []).forEach(p => { t += `• ${p}\n`; });
        t += '\n';
      }
      if (results.solution) {
        t += `## SOLUZIONE\n${results.solution.title || ''}\n${results.solution.description || ''}\n`;
        (results.solution.keyActions || []).forEach(a => { t += `• ${a}\n`; });
        t += '\n';
      }
      if (results.results) {
        t += `## RISULTATI\n${results.results.title || ''}\n${results.results.description || ''}\n`;
        (results.results.metrics || []).forEach(m => { t += `• ${m.label}: ${m.value} (${m.context})\n`; });
        (results.results.qualitativeResults || []).forEach(r => { t += `• ${r}\n`; });
      }
      if (results.testimonial?.quote) t += `\n> "${results.testimonial.quote}" — ${results.testimonial.author}, ${results.testimonial.role}\n`;
      if (results.ctaMessage) t += `\n---\n${results.ctaMessage}`;
      if (entryCtaUrl) t += `\n→ ${entryCtaUrl}`;
      return t;
    }
    case 'social': {
      const ch = entry.params?.socialChannel || 'instagram';
      switch (ch) {
        case 'x': {
          if (results.isThread && results.thread?.length > 0) {
            return results.thread.map(tw => `[${tw.number}/${results.thread.length}] ${tw.text}`).join('\n\n');
          }
          let t = results.tweet || '';
          if (results.hashtags?.length) t += ' ' + results.hashtags.map(h => '#' + h).join(' ');
          return t;
        }
        case 'instagram': {
          let t = results.caption || '';
          if (results.firstCommentHashtags?.length) t += '\n\n--- Primo commento (hashtag) ---\n' + results.firstCommentHashtags.map(h => '#' + h).join(' ');
          return t;
        }
        case 'pinterest':
          return `${results.pinTitle || ''}\n\n${results.pinDescription || ''}\n\n${(results.hashtags || []).map(h => '#' + h).join(' ')}`;
        case 'tiktok': {
          let t = results.caption || '';
          if (results.script) t += '\n\n--- Script parlato ---\n' + results.script;
          if (results.hashtags?.length) t += '\n\n' + results.hashtags.map(h => '#' + h).join(' ');
          return t;
        }
        default: {
          let t = results.post || '';
          if (results.hashtags?.length) t += '\n\n' + results.hashtags.map(h => '#' + h).join(' ');
          if (results.firstComment) t += '\n\n--- Primo commento ---\n' + results.firstComment;
          if (entryCtaUrl) t += '\n\n→ ' + entryCtaUrl;
          return t;
        }
      }
    }
    case 'landing':
    case 'ads':
    default: {
      // eslint-disable-next-line no-unused-vars
      const { _meta, ...clean } = results;
      return JSON.stringify(clean, null, 2);
    }
  }
}

// ─── Blog HTML Builder ────────────────────────────────────────────────────────

export function getBlogHtmlForEntry(entry, sanitizeHtml) {
  const results     = entry.results;
  const entryCtaUrl = normalizeUrl(entry.params?.ctaUrl);
  if (!results) return '';
  if (entry.seoHtml) return sanitizeHtml(entry.seoHtml);

  let html = '';
  html += `<p>${(results.introduction || '').replace(/\n/g, '</p>\n<p>')}</p>\n\n`;
  (results.sections || []).forEach(s => {
    html += `<h2>${s.h2}</h2>\n<p>${(s.content || '').replace(/\n/g, '</p>\n<p>')}</p>\n\n`;
    (s.subsections || []).forEach(sub => {
      html += `<h3>${sub.h3}</h3>\n<p>${(sub.content || '').replace(/\n/g, '</p>\n<p>')}</p>\n\n`;
    });
  });
  html += `<h2>Conclusione</h2>\n<p>${(results.conclusion || '').replace(/\n/g, '</p>\n<p>')}</p>\n`;
  if (entryCtaUrl) html += `\n<p><a href="${entryCtaUrl}" target="_blank" rel="noopener" style="color:#6366F1;font-weight:600">Scopri di più →</a></p>\n`;
  return sanitizeHtml(html);
}
